/**
 * Table row for a partner.
 */
import * as React from 'react';
import { Building2, MapPin, Globe, Mail } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { PARTNER_TYPE_CONFIG } from './partner-configs';
import { stripProtocol } from './partner-table-utils';
import type { Partner } from '@/types/partner.types';

/** Props for {@link PartnerRow}. */
interface PartnerRowProps {
  partner: Partner;
  pageActions?: {
    canView?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
  };
  onView?: (partner: Partner) => void;
  onEdit?: (partner: Partner) => void;
  onDelete?: (id: string) => void;
}

/** Table row for a partner. */
export const PartnerRow: React.FC<PartnerRowProps> = ({
  partner,
  pageActions,
  onView,
  onEdit,
  onDelete,
}) => {
  const canView = pageActions?.canView ?? true;
  const canEdit = pageActions?.canEdit ?? true;
  const canDelete = pageActions?.canDelete ?? true;

  const typeConfig = PARTNER_TYPE_CONFIG;
  const TypeIcon = typeConfig.icon;

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Building2 className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-foreground truncate">{partner.name}</span>
            {partner.description && (
              <span className="text-xs text-muted-foreground truncate max-w-xs">
                {partner.description}
              </span>
            )}
          </div>
        </div>
      </td>

      <td>
        {partner.city ? (
          <div className="flex items-center gap-1.5 text-sm text-foreground">
            <MapPin className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <span>{partner.city}</span>
            {partner.department && (
              <span className="text-muted-foreground">({partner.department})</span>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td>
        {partner.website ? (
          <div className="flex items-center gap-1.5 text-sm">
            <Globe className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <a
              href={partner.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--color-2-nuance-1)] hover:underline truncate max-w-[160px]"
              onClick={(e) => e.stopPropagation()}
            >
              {stripProtocol(partner.website)}
            </a>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td>
        {partner.contactEmail ? (
          <div className="flex items-center gap-1.5 text-sm">
            <Mail className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
            <a
              href={`mailto:${partner.contactEmail}`}
              className="text-foreground hover:text-[var(--color-2-nuance-1)] truncate max-w-[180px]"
              onClick={(e) => e.stopPropagation()}
            >
              {partner.contactEmail}
            </a>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>

      <td className="text-center">
        <LabelBadge
          label={typeConfig.label}
          icon={TypeIcon}
          variant={typeConfig.badgeVariant}
          color={typeConfig.badgeColor}
          size="sm"
        />
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          {canView && onView && <Button preset="row-view" onClick={() => onView(partner)} />}
          {canEdit && onEdit && <Button preset="row-edit" onClick={() => onEdit(partner)} />}
          {canDelete && onDelete && (
            <Button preset="row-delete" onClick={() => onDelete(partner.id)} />
          )}
        </div>
      </td>
    </TableRow>
  );
};
